import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from './components/ui/sonner';

// Pages
import LandingPage from './pages/LandingPage';
import AuthCallback from './pages/AuthCallback';
import ResearcherDashboard from './pages/ResearcherDashboard';
import ClientDashboard from './pages/ClientDashboard';
import BrowseOpportunities from './pages/BrowseOpportunities';
import BrowseProfiles from './pages/BrowseProfiles';
import OpportunityDetail from './pages/OpportunityDetail';
import ProfileDetail from './pages/ProfileDetail';
import MyProfile from './pages/MyProfile';
import MyApplications from './pages/MyApplications';
import SavedOpportunities from './pages/SavedOpportunities';
import MyOpportunities from './pages/MyOpportunities';
import CreateOpportunity from './pages/CreateOpportunity';
import Messages from './pages/Messages';
import BrowseBriefs from './pages/BrowseBriefs';
import BriefDetail from './pages/BriefDetail';
import MyBriefs from './pages/MyBriefs';
import CreateBrief from './pages/CreateBrief';
import MyInterests from './pages/MyInterests';
import Verification from './pages/Verification';
import AboutPage from './pages/AboutPage';
import EmailAuth from './pages/EmailAuth';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminModeration from './pages/AdminModeration';
import AdminVerifications from './pages/AdminVerifications';

import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance
export const api = axios.create({
  baseURL: API,
  withCredentials: true
});

// Attach session token from localStorage to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth Provider Component
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = (role) => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    // Store role for after auth
    localStorage.setItem('pendingRole', role);
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    localStorage.removeItem('pendingRole');
    localStorage.removeItem('session_token');
  };

  const updateRole = async (newRole) => {
    try {
      const response = await api.post('/auth/update-role', { role: newRole });
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Update role error:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, updateRole, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

// Protected Route Component
function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-neutral-800"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard based on role
    const redirectPath = user.role === 'admin' 
      ? '/admin/dashboard' 
      : user.role === 'researcher' 
        ? '/researcher/dashboard' 
        : '/client/dashboard';
    return <Navigate to={redirectPath} replace />;
  }

  return children;
}

// App Router Component
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/auth/email" element={<EmailAuth />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Researcher Routes */}
      <Route path="/researcher/dashboard" element={
        <ProtectedRoute allowedRoles={['researcher']}>
          <ResearcherDashboard />
        </ProtectedRoute>
      } />
      <Route path="/researcher/profile" element={
        <ProtectedRoute allowedRoles={['researcher']}>
          <MyProfile />
        </ProtectedRoute>
      } />
      <Route path="/researcher/opportunities" element={
        <ProtectedRoute allowedRoles={['researcher']}>
          <BrowseOpportunities />
        </ProtectedRoute>
      } />
      <Route path="/researcher/saved" element={
        <ProtectedRoute allowedRoles={['researcher']}>
          <SavedOpportunities />
        </ProtectedRoute>
      } />
      <Route path="/researcher/applications" element={
        <ProtectedRoute allowedRoles={['researcher']}>
          <MyApplications />
        </ProtectedRoute>
      } />
      <Route path="/researcher/messages" element={
        <ProtectedRoute allowedRoles={['researcher']}>
          <Messages />
        </ProtectedRoute>
      } />
      <Route path="/researcher/briefs" element={
        <ProtectedRoute allowedRoles={['researcher']}>
          <BrowseBriefs />
        </ProtectedRoute>
      } />
      <Route path="/researcher/interests" element={
        <ProtectedRoute allowedRoles={['researcher']}>
          <MyInterests />
        </ProtectedRoute>
      } />
      <Route path="/researcher/verification" element={
        <ProtectedRoute allowedRoles={['researcher']}>
          <Verification />
        </ProtectedRoute>
      } />
      
      {/* Client Routes */}
      <Route path="/client/dashboard" element={
        <ProtectedRoute allowedRoles={['client']}>
          <ClientDashboard />
        </ProtectedRoute>
      } />
      <Route path="/client/opportunities" element={
        <ProtectedRoute allowedRoles={['client']}>
          <MyOpportunities />
        </ProtectedRoute>
      } />
      <Route path="/client/opportunities/new" element={
        <ProtectedRoute allowedRoles={['client']}>
          <CreateOpportunity />
        </ProtectedRoute>
      } />
      <Route path="/client/profiles" element={
        <ProtectedRoute allowedRoles={['client']}>
          <BrowseProfiles />
        </ProtectedRoute>
      } />
      <Route path="/client/messages" element={
        <ProtectedRoute allowedRoles={['client']}>
          <Messages />
        </ProtectedRoute>
      } />
      <Route path="/client/briefs" element={
        <ProtectedRoute allowedRoles={['client']}>
          <MyBriefs />
        </ProtectedRoute>
      } />
      <Route path="/client/briefs/new" element={
        <ProtectedRoute allowedRoles={['client']}>
          <CreateBrief />
        </ProtectedRoute>
      } />
      
      {/* Shared Routes - Public */}
      <Route path="/opportunity/:id" element={<OpportunityDetail />} />
      <Route path="/profile/:id" element={<ProfileDetail />} />
      <Route path="/brief/:id" element={<BriefDetail />} />
      
      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/moderation" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminModeration />
        </ProtectedRoute>
      } />
      <Route path="/admin/verifications" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminVerifications />
        </ProtectedRoute>
      } />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
